import React from 'react';
import styled from '@emotion/styled';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';

import { dialogContentsState } from '@components/templates/Dialog';
import { Input, PrimaryButton } from '@components/atoms';
import { Sub1Button } from '@components/atoms';
import { getFetcher } from '../../../../App';
import { ErrorCode, ErrorMessage } from '@variables/error';
import { IdValidationStyle, PwValidationStyle } from '@pages/Login/style';
import he from 'he';
import { submitDebounce } from '../../../../utils/lib';

const MailSettingField = ({
  accessToken,
  fromAddress,
  fromName,
  host,
  port,
  userName,
  userPassword = '',
  testEmail = '',
  onChange = (e: any) => {
    return;
  },
}: any) => {
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const checkValidation = () => {
    return fromAddress && fromName && host && port && userName && userPassword;
  };
  const sendTestMail = async () => {
    if (!checkValidation()) return;
    const response = await getFetcher({
      method: 'POST',
      accessToken,
      body: {
        fromAddress,
        fromName,
        host,
        port,
        userName,
        userPassword,
      },
    })(`api/sw/email/verify?testEmail=${testEmail}`);
    if (response.code !== '200') {
      let header = '';
      let body = '';
      if (ErrorCode[response.code]) {
        header = `${ErrorCode[response.code]}`;
        body = `${ErrorMessage[ErrorCode[response.code]]}`;
      } else {
        header = response.code;
      }

      setDialogContents({
        type: 'error',
        header: `${header}`,
        body: <>{body}</>,
        footer: <PrimaryButton onClick={() => setDialogContents({ type: 'hidden' })}>OK</PrimaryButton>,
      });
    }
  };
  return (
    <MailSettingsWrapper>
      <Input
        className={'ip'}
        type="text"
        placeholder="SMTP Server"
        name={'host'}
        value={host ? he.decode(host) : ''}
        onChange={onChange}
        styles={css`
          ${CommonValidationStyle};
          ${ValueValidationStyle};
        `}
        onBlur={(e) => {
          if (!e.target.value) {
            e.target.dataset['invalid'] = 'none';
          } else {
            e.target.dataset['invalid'] = '';
          }
        }}
        required
      />
      <Input
        className={'port'}
        type="text"
        placeholder="Port"
        name={'port'}
        value={port || ''}
        onChange={onChange}
        styles={css`
          ${CommonValidationStyle};
          ${ValueValidationStyle};
        `}
        onBlur={(e) => {
          if (!e.target.value) {
            e.target.dataset['invalid'] = 'none';
          } else {
            e.target.dataset['invalid'] = '';
          }
        }}
        required
      />
      <Input
        type="text"
        placeholder="From Name"
        name={'fromName'}
        value={fromName ? he.decode(fromName) : ''}
        onChange={onChange}
        styles={css`
          ${CommonValidationStyle};
          ${ValueValidationStyle};
        `}
        onBlur={(e) => {
          if (!e.target.value) {
            e.target.dataset['invalid'] = 'none';
          } else {
            e.target.dataset['invalid'] = '';
          }
        }}
        required
      />
      <Input
        type="text"
        placeholder="From Address"
        name={'fromAddress'}
        value={he.decode(fromAddress || '')}
        onChange={onChange}
        styles={css`
          ${CommonValidationStyle};
          ${ValueValidationStyle};
        `}
        onBlur={(e) => {
          if (!e.target.value) {
            e.target.dataset['invalid'] = 'none';
          } else {
            e.target.dataset['invalid'] = '';
          }
        }}
        required
      />
      <Input
        type="text"
        placeholder="Reply to Address"
        value={testEmail ? he.decode(testEmail) : ''}
        name={'testEmail'}
        onChange={onChange}
        styles={css`
          input:disabled {
            & ~ span:before {
              display: none !important;
            }
          }
        `}
      />
      <Input
        type="text"
        placeholder="User Name"
        name={'userName'}
        value={userName ? he.decode(userName) : ''}
        onChange={onChange}
        styles={css`
          ${CommonValidationStyle};
          ${IdValidationStyle};
        `}
        onBlur={(e) => {
          if (!e.target.value) {
            e.target.dataset['invalid'] = 'none';
          } else {
            e.target.dataset['invalid'] = '';
          }
        }}
        required
      />
      <Input
        type="password"
        name={'userPassword'}
        placeholder="Password"
        value={userPassword || ''}
        onChange={onChange}
        styles={css`
          ${CommonValidationStyle};
          ${PwValidationStyle};
        `}
        onBlur={(e) => {
          if (!e.target.value) {
            e.target.dataset['invalid'] = 'true';
          } else {
            e.target.dataset['invalid'] = '';
          }
        }}
        required
      />
      <Input
        type="checkbox"
        name="Use SSL"
        styles={css`
          visibility: hidden;
          input:disabled {
            & ~ span:before {
              display: none !important;
            }
          }
        `}
        disabled
      >
        Use SSL
      </Input>
      <Sub1Button
        styles={css`
          width: 206px;
          height: 42px;
          font-size: 18px;
        `}
        data-name="Send test mail"
        onClick={submitDebounce(sendTestMail)}
        disabled={!checkValidation()}
      >
        Send test mail
      </Sub1Button>
    </MailSettingsWrapper>
  );
};

export const MailSettingsWrapper = styled.fieldset`
  width: 100%;
  display: grid;
  grid-template-columns: 1.23fr 0.3846fr 0.3846fr;
  grid-template-rows: repeat(40px);
  grid-gap: 19px;
  border: none;
  margin: 0 30px 0 60px;
  & > div {
    height: 40px;
    grid-column: span 3;
    &[data-name='host' i] {
      grid-column: span 2;
    }
    &[data-name='Port' i] {
      grid-column: span 1;
    }
    &[data-name='Use SSL' i] {
      grid-column: span 2;
    }
  }
  & > button {
    &[data-name='Send test mail' i] {
      grid-column: span 1;
      margin-left: auto;
    }
  }
`;

const CommonValidationStyle = css`
  input[data-invalid$='true' i]:invalid,
  input[data-invalid='same-with-id' i],
  input[data-invalid='short' i],
  input[data-invalid='invalid'] {
    border-color: #ff4c4c !important;
    position: relative;
    & ~ span {
      color: #ff4c4c;
    }
    & ~ div[data-type='validate'] {
      display: block !important;
      width: 100%;
      &::after {
        color: #ff4c4c;
        font-size: 13px;
        text-align: left;
        position: absolute;
        left: 0;
        top: 100%;
        width: 100%;
      }
    }
  }
`;

export const ValueValidationStyle = css`
  input[data-invalid='none' i]:invalid {
    border: 1px solid #ff4c4c !important;
    position: relative;
    & ~ div[data-type='validate'] {
      display: block !important;
      width: 100%;
      &::after {
        content: 'Please enter the Value.';
        color: #ff4c4c;
        font-size: 13px;
        position: absolute;
        top: 100%;
        height: 30px;
      }
    }
  }
`;

export default MailSettingField;
